import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, X, Check, Loader2, Clock, LogIn } from 'lucide-react';
import { notificationsAPI } from '../services/api';
import { walkInApi } from '../services/accessApi';
import {
  closeWalkInModal,
  resolveWalkInModal,
  subscribeWalkInModal,
  type WalkInModalInfo,
} from '../services/walkInModal';

/**
 * App-level "Solicitud de acceso sin QR" modal (2026-09-16, Jaime feature).
 * Mounted ONCE in App.tsx (next to PlanLimitModal); fed by the shared
 * walkInModal store that AppShell polling + Notifications UI surface into.
 * Mobile-safe: fixed overlay, full-width card, big tap targets — same overlay
 * pattern as PlanLimitModal which is already used on phones.
 */
export function WalkInModal() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<WalkInModalInfo | null>(null);
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);
  const [result, setResult] = useState<'approved' | 'denied' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsub = subscribeWalkInModal((next) => {
      setInfo(next);
      // New request opens/replaces in place: reset per-modal UI state.
      if (next) { setResult(null); setError(null); setBusy(null); }
    });
    return unsub;
  }, []);

  if (!info) return null;

  const decide = async (action: 'approve' | 'deny') => {
    if (busy || result) return;
    setBusy(action);
    setError(null);
    try {
      await (action === 'approve' ? walkInApi.approve(info.walkInId) : walkInApi.deny(info.walkInId));
      setResult(action === 'approve' ? 'approved' : 'denied');
      // Best-effort: mark the originating notification read + refresh badge.
      try {
        await notificationsAPI.markAsRead(info.notificationId);
        window.dispatchEvent(new Event('vecindus:notifications-updated'));
      } catch { /* badge refresh is cosmetic */ }
      window.setTimeout(() => resolveWalkInModal(), 1400);
    } catch (err: any) {
      setBusy(null);
      const msg = String(err?.response?.data?.error || err?.response?.data?.message || '');
      // Stale decisions (already approved/denied/expired by another member or
      // the 15-min timeout) must surface, not silently fail.
      setError(
        /already|expired|expirad|timeout|cancel/i.test(msg) || err?.response?.status === 409 || err?.response?.status === 400
          ? (msg || 'Esta solicitud ya fue decidida o expiró.')
          : (msg || 'No se pudo procesar la solicitud.')
      );
      window.dispatchEvent(new Event('vecindus:notifications-updated'));
    }
  };

  const dismiss = () => {
    closeWalkInModal(); // badge + /notifications still surface it afterwards
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      aria-label="Solicitud de acceso sin QR"
    >
      <div
        className="relative bg-[var(--v-surface-1)] rounded-2xl shadow-xl w-full max-w-md p-6 border-2 border-amber-300 dark:border-amber-700"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[var(--v-surface-2)] text-gray-400 hover:text-gray-600"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-100 flex items-center justify-center shrink-0">
            <UserPlus className="w-5 h-5 text-cyan-600" />
          </div>
          <div className="min-w-0">
            <h2 className="font-bold text-lg text-[#2D3748] leading-tight">Solicitud de acceso sin QR</h2>
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-0.5">
              <Clock className="w-3 h-3" /> Responde en 15 minutos o expira automáticamente
            </p>
          </div>
        </div>

        {result ? (
          <div className={`flex items-center gap-2 rounded-lg px-4 py-3 mb-4 text-sm font-semibold ${result === 'approved' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {result === 'approved' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
            {result === 'approved' ? 'Visita aprobada — el código quedó activo.' : 'Visita rechazada.'}
          </div>
        ) : (
          <div className="mb-4">
            <p className="text-lg font-semibold text-[#2D3748] truncate">{info.visitorName}</p>
            {info.unitLabel && <p className="text-sm text-gray-500">Unidad: {info.unitLabel}</p>}
            {info.detail && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{info.detail}</p>}
          </div>
        )}

        {error && (
          <div className="mb-4 text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2">
            {error}
            <button onClick={dismiss} className="ml-2 underline font-medium">Entendido</button>
          </div>
        )}

        {!result && (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => decide('approve')}
              disabled={!!busy}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            >
              {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Aprobar
            </button>
            <button
              type="button"
              onClick={() => decide('deny')}
              disabled={!!busy}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            >
              {busy === 'deny' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Rechazar
            </button>
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={dismiss}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Ahora no
          </button>
          <button
            type="button"
            onClick={() => { closeWalkInModal(); navigate('/my-qr'); }}
            className="text-sm font-bold text-[#36CCD8] hover:underline flex items-center gap-1"
          >
            Ver detalles <LogIn className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default WalkInModal;
